import { useState } from "react"
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import logger from './utils/logger.js';


export default function FeedbackForm() {
  const [ratings, setRatings] = useState({
    Overall: 0,
    Sarah_flow: 0, 
    Question_Level: 0,
    Technical_Stability: 0
  });

  const [hoverRatings, setHoverRatings] = useState({
    Overall: 0,
    Sarah_flow: 0,
    Question_Level: 0,
    Technical_Stability: 0
  });

  const [missingRatings, setMissingRatings] = useState({
    Overall: false,
    Sarah_flow: false,
    Question_Level: false,
    Technical_Stability: false
  });

  const [feedback, setFeedback] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const job_interview_id = params.job_interview_id || location.state?.job_interview_id || localStorage.getItem('job_interview_id');
  const BASE_URL = import.meta.env.VITE_APP_BASE_URL;

  // Add function to calculate progress
  const calculateProgress = () => {
    const total = Object.keys(ratings).length;
    const completed = Object.values(ratings).filter(rating => rating > 0).length;
    return (completed / total) * 100;
  };

  const handleRatingChange = (category, value) => {
    setRatings(prev => ({ ...prev, [category]: value }));
    setMissingRatings(prev => ({ ...prev, [category]: false }));
    setShowError(false);
  };

  const handleHoverRating = (category, value) => {
    setHoverRatings(prev => ({ ...prev, [category]: value }));
  };

  const hasAnyRating = () => {
    return Object.values(ratings).every(rating => rating > 0);
  };

  const handleDisabledSubmitClick = () => {
    const updatedMissing = {};
    let hasMissing = false;
    const missingCategories = [];
    
    for (const key in ratings) {
      const isMissing = ratings[key] === 0;
      updatedMissing[key] = isMissing;
      if (isMissing) {
        hasMissing = true;
        missingCategories.push(key.replace(/_/g, ' '));
      }
    }
    
    setMissingRatings(updatedMissing);
    setShowError(true);
    // setErrorMessage(`Please provide ratings for: ${missingCategories.join(', ')}`);
  };

  const ratingDescriptions = {
    Overall: "Your overall experience with the interview",
    Sarah_flow: "Experience with interview flow and structure",
    Question_Level: "Appropriateness of question difficulty level",
    Technical_Stability: "Technical stability of the platform"
  };

  const handleSubmit = async () => {
    if (!hasAnyRating()) {
      handleDisabledSubmitClick();
      return;
    }
    
    setIsSubmitting(true);
    setShowError(false);
    setErrorMessage("");
    setMissingRatings({
      Overall: false,
      Sarah_flow: false,
      Question_Level: false,
      Technical_Stability: false
    });

    if (!job_interview_id) {
      setIsSubmitting(false);
      toast.error("Error: No interview ID found. Please contact support.", {
        duration: 4000,
        position: "top-center"
      });
      return;
    }

    try {
      const response = await fetch(`https://${BASE_URL}/api/jobinterviews/candidate-feedback/${job_interview_id}`, {
        method: 'PUT',
       // credentials: "include",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          candidate_remark: {
            ...ratings,
            Any_other_feedback: feedback || "No additional feedback provided"
          }
        }),
      });

      if (response.ok) {
        navigate('/thankyou');
      } else {
        toast.error(`Error submitting feedback (${response.status}). Please try again.`, {
          duration: 4000,
          position: "top-center"
        });
      }
    } catch (error) {
      logger.error('Error submitting feedback:', error);
      toast.error('Error submitting feedback. Please check your connection and try again.', {
        duration: 4000,
        position: "top-center"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = (category) => {
    const isMissing = showError && missingRatings[category];
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => {
          const filled = star <= (hoverRatings[category] || ratings[category]);
          const color = filled
            ? "#FBBF24"
            : isMissing
              ? "#EF4444"
              : "#D1D5DB";

          return (
            <button
              key={star}
              className="focus:outline-none"
              onMouseEnter={() => handleHoverRating(category, star)}
              onMouseLeave={() => handleHoverRating(category, 0)}
              onClick={() => handleRatingChange(category, star)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={window.innerWidth < 700 ? "18" : "24"}
                height={window.innerWidth < 700 ? "18" : "24"}
                viewBox="0 0 24 24"
                fill="none"
                stroke={color}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="transition-colors"
              >
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className={`${window.innerWidth < 700 ? 'w-[90%]' : 'w-[600px]'} bg-white rounded-[16px] shadow-xl`}>
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white p-4 flex justify-between items-start">
          <h2 className="text-lg font-semibold text-left text-black tracking-wider">Feedback</h2>
        </div>

        {/* Content */}
        <div className="space-y-6">
          <div className="flex flex-col items-center text-center space-y-4 p-6" style={{ background: 'linear-gradient(to bottom, #DCFCE8, rgba(255, 255, 255, 0))' }}>
            <div className={`${window.innerWidth < 700 ? 'w-12 h-12' : 'w-14 h-14'} bg-emerald-100 rounded-full flex items-center justify-center`}>
              <div className={`${window.innerWidth < 700 ? 'w-8 h-8' : 'w-10 h-10'} bg-emerald-500 rounded-full flex items-center justify-center`}>
                <svg xmlns="http://www.w3.org/2000/svg" width={window.innerWidth < 700 ? "16" : "20"} height={window.innerWidth < 700 ? "16" : "20"} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
            </div>

            <div className="space-y-2">
              <h1 className={`${window.innerWidth < 700 ? 'text-lg' : 'text-xl'} font-semibold text-emerald-700`}>Thank you for taking this interview!</h1>
              <p className={`${window.innerWidth < 700 ? 'text-xs' : 'text-sm'} text-gray-600 max-w-sm`}>Our team must be grading your performance at this moment. Please allow us some time to get back to you on this.</p>
            </div>
          </div>

          <div className={`px-${window.innerWidth < 700 ? '4' : '6'} pb-4`}>
            <div className="space-y-5">
              <div className="flex flex-col items-center gap-2">
                <h2 className={`${window.innerWidth < 700 ? 'text-base' : 'text-lg'} font-semibold text-gray-700 text-center`}>Please rate your experience</h2>
                
                {/* Progress Bar */}
                <div className="w-full max-w-xs bg-gray-200 rounded-full h-2.5">
                  <div 
                    className="bg-emerald-500 h-2.5 rounded-full transition-all duration-300 ease-in-out"
                    style={{ width: `${calculateProgress()}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500">
                  {Object.values(ratings).filter(rating => rating > 0).length} of {Object.keys(ratings).length} categories rated
                </p>
              </div>

              <div className="overflow-y-auto max-h-[40vh] pr-1 space-y-4">
                {Object.keys(ratings).map((category) => (
                  <div key={category} className="flex items-center justify-between">
                    <div className="space-y-1">
                      <h3 className={`${window.innerWidth < 700 ? 'text-sm' : 'text-base'} font-medium text-gray-700 flex items-center gap-2`}>
                        {category.replace(/_/g, ' ')}
                        {showError && missingRatings[category] ? (
                          <span className="text-red-500 text-xs flex items-center gap-1 animate-fadeIn">
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"/>
                              <line x1="12" y1="8" x2="12" y2="12"/>
                              <line x1="12" y1="16" x2="12.01" y2="16"/>
                            </svg>
                            Required
                          </span>
                        ) : ratings[category] > 0 && (
                          <span className="text-emerald-500 text-xs flex items-center gap-1 animate-fadeIn">
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M20 6L9 17l-5-5"/>
                            </svg>
                            Completed
                          </span>
                        )}
                      </h3>
                      <p className={`${window.innerWidth < 700 ? 'text-xs' : 'text-sm'} text-gray-500`}>{ratingDescriptions[category]}</p>
                    </div>
                    {renderStars(category)}
                  </div>
                ))}
              </div>

              

              <div className="space-y-2 pt-2">
                <h3 className={`${window.innerWidth < 700 ? 'text-base' : 'text-lg'} font-medium text-gray-700`}>Any other feedback?</h3>
                <textarea
                  placeholder="Share any additional thoughts or suggestions..."
                  className={`w-full ${window.innerWidth < 700 ? 'h-20' : 'h-24'} p-3 text-sm text-gray-900 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500`}
                  style={{ background: 'var(--Gray-50, rgba(250, 250, 250, 1))', border: '1px solid var(--Gray-200, rgba(229, 231, 235, 1))' }}
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 z-10 bg-gray-50 p-4 rounded-b-xl">
          <button 
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`w-full ${window.innerWidth < 700 ? 'h-9 text-xs' : 'h-10 text-sm'} font-medium rounded-lg flex items-center justify-center transition-all text-white active:scale-95 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            style={{
              background: 'linear-gradient(0deg, var(--Green-700, #15803C), var(--Green-700, #15803C))',
              border: '1px solid rgba(255, 255, 255, 0.32)',
              boxShadow: '0px 6px 13px rgba(0, 0, 0, 0.15), 0px 23px 23px rgba(0, 0, 0, 0.13), 0px 53px 32px rgba(0, 0, 0, 0.08), 0px 94px 38px rgba(0, 0, 0, 0.02), 0px 147px 41px rgba(0, 0, 0, 0)'
            }}
          >
            {isSubmitting ? (
              <svg className={`animate-spin ${window.innerWidth < 700 ? 'h-4 w-4' : 'h-5 w-5'} text-white`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <>
                Submit feedback
                <svg xmlns="http://www.w3.org/2000/svg" width={window.innerWidth < 700 ? "14" : "16"} height={window.innerWidth < 700 ? "14" : "16"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}